<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

const props = defineProps({
  payments: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const { t } = useI18n();
const canvasRef = ref(null);
let chartInstance = null;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'];

const chartData = computed(() => ({
  labels: props.payments.map(p => p.payment_method),
  datasets: [
    {
      data: props.payments.map(p => p.count),
      backgroundColor: props.payments.map((_, i) => COLORS[i % COLORS.length]),
      borderWidth: 0,
    },
  ],
}));

watch(
  [() => props.payments, canvasRef],
  () => {
    if (!canvasRef.value || !props.payments.length) return;
    if (chartInstance) chartInstance.destroy();
    chartInstance = new Chart(canvasRef.value, {
      type: 'doughnut',
      data: chartData.value,
      options: {
        responsive: true,
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              label: ctx => {
                const item = props.payments[ctx.dataIndex];
                return ` ${item.count} vendas (${item.percentage}%)`;
              },
            },
          },
        },
      },
    });
  },
  { immediate: false }
);
</script>

<template>
  <div>
    <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">
      {{ t('PDV_SALES_REPORT.PAYMENT_BREAKDOWN.TITLE') }}
    </h3>
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-6 text-slate-500"
    >
      <span class="i-lucide-loader-circle animate-spin mr-2" />
    </div>
    <div
      v-else-if="!payments.length"
      class="flex items-center justify-center py-6 text-slate-400 text-sm"
    >
      {{ $t('PDV_SALES_REPORT.NO_DATA') }}
    </div>
    <canvas v-else ref="canvasRef" class="max-h-56" />
  </div>
</template>
